import { NavLink as RouterNavLink, useLocation } from 'react-router-dom'
import type { BrandThemeIcon } from '@jotta/types/Brand'
import { useBrandStore } from '@jotta/ui/useBrandTheme'
import { AccountType } from '@jotta/grpc-js-client/customerService'
import { Onboarding } from '@jotta/ui/Onboarding'
import { t, Trans } from '@lingui/macro'
import { observer } from 'mobx-react-lite'
import { showIntercom } from '@jotta/intercom'
import { useBrandingInfo } from '@jotta/grpc-connect-client/config'
import { useGetCustomer } from '@jotta/grpc-connect-client/customer'
import { cn } from '@jotta/utils/css'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import type * as React from 'react'
import type { MouseEvent, ReactNode } from 'react'
import SvgTrash from '@jotta/ui/SvgTrash'
import SvgSettingsMenuItem from '@jotta/ui/SvgSettingsMenuItem'
import SvgBusinessMenuItem from '@jotta/ui/SvgBusinessMenuItem'
import SvgPhotos from '@jotta/ui/SvgPhotos'
import SvgStar from '@jotta/ui/SvgStar'
import SvgHelpMenuItem from '@jotta/ui/SvgHelpMenuItem'
import SvgSync from '@jotta/ui/SvgSync'
import SvgBackup from '@jotta/ui/SvgBackup'
import SvgArchive from '@jotta/ui/SvgArchive'
import SvgAlbum from '@jotta/ui/SvgAlbum'
import SvgEyeSlash from '@jotta/ui/SvgEyeSlash'
import SvgSharedFolder from '@jotta/ui/SvgSharedFolder'

function NavSection({
  children,
  title,
}: {
  children: ReactNode
  title?: string
}) {
  return (
    <div className="flex flex-col py-3">
      <header className="label-sm whitespace-nowrap py-1 pl-6 text-grey4">
        {title}
      </header>
      {children}
    </div>
  )
}

function NavLink({
  active,
  className,
  icon,
  children,
  ...rest
}: {
  onClick: (e: MouseEvent<HTMLElement>) => void
  to: string
  className?: string
  icon: BrandThemeIcon
  active: boolean
  children: React.ReactNode
  'data-section': string
}) {
  return (
    <RouterNavLink
      className={cn(
        'body-md-sh flex w-full items-center gap-3 whitespace-nowrap py-2 pl-6 pr-12 text-navigation hover:text-navigation-hover',
        {
          'text-navigation-pressed': active,
        },
        className,
      )}
      {...rest}
    >
      <PlainBrandIcon className="shrink-0" icon={icon} height={24} />
      {children}
    </RouterNavLink>
  )
}

export const NavBar = observer<{
  businessName?: string
  accountType?: AccountType
  userCanUpgrade?: boolean
}>(function NavBar({ businessName, accountType, userCanUpgrade }) {
  const { supportLink = '' } = useBrandingInfo()
  const intercomEnabled = Boolean(useGetCustomer().data?.intercom)
  const { openDrawer, onNavigate } = useBrandStore()

  const { pathname } = useLocation()
  const settingsButtonIsActive =
    /^\/web\/(account|secure|billing|family|connected)/.test(pathname)
  const trashButtonIsActive = /^\/web\/(trash)/.test(pathname)
  const bizButtonIsActive = /^\/(biz)/.test(pathname)
  const helpButtonIsActive = intercomEnabled || supportLink
  const upgradeButtonIsActive = /^\/web\/(upgrade)/.test(pathname)

  return (
    <nav>
      <NavSection title={t`Files`}>
        <Onboarding task="files" onOpen={openDrawer}>
          <NavLink
            onClick={onNavigate}
            data-section="files"
            to="/web/sync"
            active={pathname.startsWith('/web/sync')}
            icon={SvgSync}
          >
            <Trans>Synced</Trans>
          </NavLink>
        </Onboarding>

        <NavLink
          onClick={onNavigate}
          data-section="files"
          to="/web/backup"
          active={pathname.startsWith('/web/backup')}
          icon={SvgBackup}
        >
          <Trans>Backups</Trans>
        </NavLink>

        <NavLink
          onClick={onNavigate}
          data-section="files"
          to="/web/archive"
          active={pathname.startsWith('/web/archive')}
          icon={SvgArchive}
        >
          <Trans>Archive</Trans>
        </NavLink>
      </NavSection>

      <NavSection title={t`Media`}>
        <Onboarding task="photos" onOpen={openDrawer}>
          <NavLink
            onClick={onNavigate}
            data-section="photos"
            to="/photo"
            icon={SvgPhotos}
            active={pathname === '/photo'}
          >
            <Trans>Photos</Trans>
          </NavLink>
        </Onboarding>
        <NavLink
          onClick={onNavigate}
          data-section="photos"
          to="/photo/album"
          icon={SvgAlbum}
          active={pathname.startsWith('/photo/album')}
        >
          <Trans>Collections</Trans>
        </NavLink>
        <NavLink
          onClick={onNavigate}
          data-section="photos"
          to="/photo/all_hidden"
          icon={SvgEyeSlash}
          active={pathname.startsWith('/photo/all_hidden')}
        >
          <Trans>Hidden</Trans>
        </NavLink>
      </NavSection>

      <NavSection title={t`Shared`}>
        <NavLink
          onClick={onNavigate}
          data-section="shared_files"
          to="/web/shared"
          icon={SvgSharedFolder}
          active={pathname.startsWith('/web/shared')}
        >
          <Trans>Shared Files</Trans>
        </NavLink>
        <NavLink
          onClick={onNavigate}
          data-section="shared_photos"
          to="/photo/all_shared"
          icon={SvgSharedFolder}
          active={pathname.startsWith('/photo/all_shared')}
        >
          <Trans>Shared Photos</Trans>
        </NavLink>
      </NavSection>

      <NavSection>
        <Onboarding task="trash" onOpen={openDrawer}>
          <NavLink
            onClick={onNavigate}
            data-section="trash"
            to="/web/trash"
            icon={SvgTrash}
            active={trashButtonIsActive}
          >
            <Trans id="Trash" />
          </NavLink>
        </Onboarding>

        {helpButtonIsActive && (
          <NavLink
            active={false}
            onClick={e => {
              onNavigate()
              showIntercom(e)
            }}
            data-section="help"
            to={supportLink}
            icon={SvgHelpMenuItem}
          >
            <Trans id="ZenDeskButton.label" />
          </NavLink>
        )}

        {userCanUpgrade && (
          <NavLink
            onClick={onNavigate}
            data-section="upgrade"
            to="/web/upgrade"
            icon={SvgStar}
            active={upgradeButtonIsActive}
          >
            <Trans id="Upgrade" />
          </NavLink>
        )}

        {accountType === AccountType.BUSINESS_ADMIN && (
          <NavLink
            onClick={onNavigate}
            data-section="business"
            to="/biz"
            icon={SvgBusinessMenuItem}
            active={bizButtonIsActive}
          >
            {businessName || t`Business`}
          </NavLink>
        )}

        <NavLink
          onClick={onNavigate}
          data-section="settings"
          to="/web/account"
          icon={SvgSettingsMenuItem}
          active={settingsButtonIsActive}
        >
          <Trans id="Settings" />
        </NavLink>
      </NavSection>
    </nav>
  )
})
